import React, { useMemo } from 'react';
import { usePmsDefects, type PmsDefect } from '../../pms/pms-defects/useData';
import { StatusBreakdown, type StatusBreakdownRow } from './shared';

const SEVERITY_COLORS = [
  'bg-rose-500',
  'bg-amber-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-slate-400',
  'bg-violet-500',
];

function severityKey(row: PmsDefect): { key: string; label: string } {
  const sev = row.severity as { code?: string; name?: string } | null | undefined;
  const code = sev?.code?.trim();
  const name = sev?.name?.trim();
  if (code || name) {
    return { key: (code || name || 'unknown').toUpperCase(), label: name || code || 'Unknown' };
  }
  const status = row.status as { code?: string; name?: string } | null | undefined;
  const sCode = status?.code?.trim();
  const sName = status?.name?.trim();
  if (sCode || sName) {
    return { key: `ST:${(sCode || sName || 'unknown').toUpperCase()}`, label: sName || sCode || 'Unknown' };
  }
  return { key: 'UNKNOWN', label: 'Unspecified' };
}

const PmsDefectStatus: React.FC<{ embedded?: boolean; settings?: Record<string, unknown> }> = ({
  embedded,
}) => {
  const { data, isLoading } = usePmsDefects(undefined, 1, 200);

  const rows: StatusBreakdownRow[] = useMemo(() => {
    const counts = new Map<string, { label: string; value: number }>();
    for (const row of data ?? []) {
      const { key, label } = severityKey(row);
      const prev = counts.get(key);
      if (prev) prev.value += 1;
      else counts.set(key, { label, value: 1 });
    }
    return Array.from(counts.entries())
      .map(([key, { label, value }], idx) => ({
        key,
        label,
        value,
        color: SEVERITY_COLORS[idx % SEVERITY_COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  return (
    <StatusBreakdown
      title="PMS · Defects"
      rows={rows}
      isLoading={isLoading}
      embedded={embedded}
      viewAllPath="/pms/pms-defects"
      emptyMessage="No defects"
    />
  );
};

export default PmsDefectStatus;
